/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-lines-per-function */
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';
import { isPlaceholderEmail, RegistrationService } from '../registration/registration.service';
import { ClaimTypesEnum } from 'src/app/plasma-ui-common/enums/claim-types.enum';
import moment from 'moment';

/**
 * The address off the link that is safe to put in front of the user.
 *
 * SA-21789: the link carries the account's own `email` claim, which on an account created
 * for booking or through "add someone else" is a `…+temp@synlab.com` placeholder rather than
 * an address. It is still the key `PrivateCompleteDetailsComponent` looks the account up by,
 * so `personalInfo` keeps it - but it must reach neither the cache the form renders nor the
 * prefill the form locks its controls from, or the user is left staring at a generated
 * address in a field they cannot edit.
 */
const displayableEmail = (email: string) => (isPlaceholderEmail(email) ? '' : email);

/**
 * Keeps the values the caller supplied out of reach of the form. personalInfo.email is
 * overwritten further down the flow (blanked when the account's contact e-mail is a
 * placeholder), so the prefill is the only copy left for anything that still needs an
 * address; the fiscal code has no such second copy, but the same signal is what
 * PersonalInfoComponent's lockPrefilledControls() reads to keep a caller-supplied value out
 * of the user's reach - without recording it here, the field renders pre-filled but stays
 * editable.
 */
const rememberCallerProvidedValues = (
  registrationService: RegistrationService,
  values: Record<string, string>,
) => {
  const provided = Object.fromEntries(Object.entries(values).filter(([, value]) => !!value));
  if (!Object.keys(provided).length) return;

  registrationService.personalInfoPrefill.update(prefill => ({ ...prefill, ...provided }));
};

/**
 * SA-21780: the pending OIDC authorize URL the STS was holding, appended to this redirect for
 * the mobile app client only. Read out here rather than inline with the rest of the link's
 * values to keep the guard body inside the complexity budget.
 */
const pendingAuthorizationUrl = (route: ActivatedRouteSnapshot) =>
  route.queryParamMap.get('returnUrl') || '';

export const registrationPrivateCompleteDetailsGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
) => {
  const registrationService = inject(RegistrationService);
  const email = (route.queryParamMap.get('email') || '').replace(/ /g, '+');
  const userId = route.queryParamMap.get('userId') || '';
  const synlabId = route.queryParamMap.get('synlabId') || '';
  const fiscalCode = route.queryParamMap.get('fiscalCode') || '';
  const dateOfBirth = route.queryParamMap.get('dateOfBirth') || '';
  const fromActivation = route.queryParamMap.get('fromActivation')?.toLowerCase() === 'true';

  registrationService.fromPrivateCompleteDetails.set(true);
  // SA-21780: recorded like the rest of the link's values so the paths that hand the user back
  // to the login page can resume that authorization. Absent for web users, who keep today's
  // redirects untouched. setReturnUrl() (rather than the signal's own set()) also persists it
  // to sessionStorage, so it survives a reload that happens before either reader consumes it.
  registrationService.setReturnUrl(pendingAuthorizationUrl(route));

  if (!userId || !synlabId) return false;

  const formatDOB = (dob: string) => {
    const formattedDOB = moment(dob).isValid() ? moment(dob).format('DD.MM.YYYY') : '';

    if (formattedDOB) {
      return formattedDOB;
    }
    return '';
  };

  registrationService.registeredUserData.set({
    userId,
    synlabId,
  });

  const userInfo = {
    confirmPassword: '',
    date_of_birth: dateOfBirth,
    email,
    noFiscalCode: !fiscalCode,
    password: '',
    [ClaimTypesEnum.FISCAL_CODE]: fiscalCode,
  };

  const displayEmail = displayableEmail(email);

  if (fromActivation) {
    registrationService.fromActivation.set(fromActivation);
    registrationService.personalInfoCache.set({
      ...userInfo,
      email: displayEmail,
      date_of_birth: formatDOB(dateOfBirth),
    } as any);
  }

  registrationService.personalInfo.set(
    userInfo as any, // eslint-disable-next-line @typescript-eslint/no-explicit-any
  );

  rememberCallerProvidedValues(registrationService, {
    email: displayEmail,
    [ClaimTypesEnum.FISCAL_CODE]: fiscalCode,
  });

  return true;
};
